/**
 * The widget's half of the iframe conversation.
 *
 * `embed.js` on the host page listens for a height and resizes the frame. This
 * measures and posts it whenever the content changes — moving between steps
 * changes the height a lot, and a fixed-height iframe is either a scrollbar
 * inside a page or a block of dead space under the form.
 *
 * Does nothing when not framed, so the standalone page pays no cost.
 */

const MESSAGE_TYPE = 'yasmine:height';

function isFramed(): boolean {
  try {
    return window.self !== window.top;
  } catch {
    // A cross-origin parent throws on access, which itself means we are framed.
    return true;
  }
}

function postHeight(): void {
  const height = Math.ceil(document.documentElement.scrollHeight);
  // '*' rather than a specific origin: the widget is embedded on partner sites
  // we do not know in advance, and the message carries only a number. The HOST
  // verifies our origin on its side, which is where the check belongs.
  window.parent.postMessage({ type: MESSAGE_TYPE, height }, '*');
}

/**
 * Start reporting height. Returns a teardown function.
 *
 * ResizeObserver rather than a timer: a poll either lags a step change visibly
 * or burns battery on a phone, and this fires exactly when the layout settles.
 */
export function startHeightReporting(): () => void {
  if (!isFramed()) return () => undefined;

  postHeight();

  const observer = new ResizeObserver(() => postHeight());
  observer.observe(document.documentElement);

  // A late-loading font or image can change the height after the observer has
  // already settled.
  window.addEventListener('load', postHeight);

  return () => {
    observer.disconnect();
    window.removeEventListener('load', postHeight);
  };
}
